﻿import type { MotionPermissionResult, MotionSample } from '../types';

type DeviceMotionEventConstructorWithPermission = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>;
};

export async function requestMotionPermission(win: Window = window): Promise<MotionPermissionResult> {
  const motionCtor = (win as Window & { DeviceMotionEvent?: DeviceMotionEventConstructorWithPermission }).DeviceMotionEvent;
  if (!motionCtor) return 'unavailable';

  // Most mobile browsers require a secure context for motion sensors. Localhost remains
  // practical for development, but production should be served over HTTPS.
  const host = win.location?.hostname ?? '';
  const localDevHost = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  if (win.isSecureContext === false && !localDevHost) {
    return 'unavailable';
  }

  if (typeof motionCtor.requestPermission === 'function') {
    try {
      const result = await motionCtor.requestPermission();
      return result === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }

  return 'granted';
}

export class MotionStream {
  private listening = false;
  private readonly handleMotion = (event: DeviceMotionEvent): void => {
    const vector = event.accelerationIncludingGravity ?? event.acceleration;
    if (!vector) return;
    this.onSample({
      x: vector.x,
      y: vector.y,
      z: vector.z,
      rotationRate: event.rotationRate
        ? {
            alpha: event.rotationRate.alpha,
            beta: event.rotationRate.beta,
            gamma: event.rotationRate.gamma
          }
        : null,
      timestamp: this.now()
    });
  };

  constructor(
    private readonly onSample: (sample: MotionSample) => void,
    private readonly win: Window = window,
    private readonly now: () => number = () => performance.now()
  ) {}

  start(): void {
    if (this.listening) return;
    this.win.addEventListener('devicemotion', this.handleMotion, { passive: true });
    this.listening = true;
  }

  stop(): void {
    if (!this.listening) return;
    this.win.removeEventListener('devicemotion', this.handleMotion);
    this.listening = false;
  }

  get isListening(): boolean {
    return this.listening;
  }
}
